import React, { useState, useEffect, useMemo, useCallback, memo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeftIcon,
  WifiIcon,
  MapPinIcon,
  PhoneIcon,
  GlobeAltIcon,
  ClockIcon,
  ShareIcon,
  LockClosedIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';
import { StarIcon } from '@heroicons/react/24/solid';
import LoadingSpinner from '../Common/LoadingSpinner';
import VenueGallery from './VenueGallery';
import ReviewsSectionFull from './ReviewsSection';
import { REGION_CONFIG } from '../../config/region';
import { API_BASE_URL } from '../../utils/constants';
import { dtLocale } from '../../utils/dateLocale';

// ─── helpers ────────────────────────────────────────────────────────────────

const isMobileDevice = () =>
  /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

const priceSymbols = (level) => {
  if (!level && level !== 0) return null;
  const filled = Math.max(1, Math.min(4, level));
  return (
    <span style={{ fontSize: 14, letterSpacing: 1 }}>
      {'$'.repeat(filled)}
      <span style={{ color: '#d1d5db' }}>{'$'.repeat(4 - filled)}</span>
    </span>
  );
};

const formatDate = (dateStr, t) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  if (d.toDateString() === today.toDateString()) return t ? t('calendar.today') : 'Today';
  if (d.toDateString() === tomorrow.toDateString()) return t ? t('venue.tomorrow') : 'Tomorrow';
  return d.toLocaleDateString(dtLocale(), { month: 'short', day: 'numeric' });
};

// ─── fetch venue ─────────────────────────────────────────────────────────────

const fetchVenueBySlug = async (slug) => {
  const token = localStorage.getItem('venue_app_token');
  const response = await fetch(`${API_BASE_URL}/venues/${slug}`, {
    headers: {
      'Content-Type': 'application/json',
      'x-region': REGION_CONFIG.region || 'th',
      ...(token && { Authorization: `Bearer ${token}` }),
    },
  });
  if (!response.ok) throw new Error('Venue not found');
  const result = await response.json();
  if (result.success && result.venue) return result.venue;
  throw new Error('Venue data missing');
};

// ─── sub-components ──────────────────────────────────────────────────────────

// Share button
const ShareButton = memo(({ venue, venueSlug }) => {
  const { t } = useTranslation();
  const [toast, setToast] = useState('');

  const handleShare = async () => {
    const url = `${window.location.origin}/venues/${venueSlug}`;
    const shareData = {
      title: venue?.name || t('venue.venueFallback'),
      text: t('venue.checkOut', { name: venue?.name }),
      url,
    };
    try {
      if (navigator.share && isMobileDevice()) {
        await navigator.share(shareData);
      } else {
        await navigator.clipboard.writeText(url);
        setToast(t('common.linkCopied'));
        setTimeout(() => setToast(''), 2000);
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        setToast(t('venue.couldNotShare'));
        setTimeout(() => setToast(''), 2000);
      }
    }
  };

  return (
    <>
      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={handleShare}
        style={{
          width: 44, height: 44, borderRadius: '50%',
          background: 'rgba(255,255,255,.25)', border: 'none',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          backdropFilter: 'blur(8px)', cursor: 'pointer',
        }}
      >
        <ShareIcon style={{ width: 22, color: '#fff' }} />
      </motion.button>
      <AnimatePresence>
        {toast && (
          <motion.div
            initial={{ opacity: 0, y: 50 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 50 }}
            style={{
              position: 'fixed', bottom: 80, left: '50%', transform: 'translateX(-50%)',
              background: '#111827', color: '#fff', padding: '12px 24px',
              borderRadius: 12, fontSize: 14, fontWeight: 600, zIndex: 1000,
              boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
            }}
          >
            {toast}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
});
ShareButton.displayName = 'ShareButton';

// Sticky header
const StickyHeader = memo(({ onBack, venue, venueSlug }) => (
  <motion.div
    initial={{ y: -60, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
    style={{
      position: 'sticky', top: 0, padding: 20, zIndex: 50,
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      pointerEvents: 'auto',
    }}
  >
    <motion.button
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); onBack(); }}
      whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
      style={{
        width: 44, height: 44, borderRadius: '50%',
        background: 'rgba(255,255,255,.25)', border: 'none',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        backdropFilter: 'blur(8px)', cursor: 'pointer',
        WebkitTapHighlightColor: 'transparent',
      }}
    >
      <ArrowLeftIcon style={{ width: 24, color: '#fff' }} />
    </motion.button>
    <ShareButton venue={venue} venueSlug={venueSlug} />
  </motion.div>
));
StickyHeader.displayName = 'StickyHeader';

// Address — only renders if there is any address data; NO fallbacks
const VenueAddress = memo(({ venue }) => {
  const parts = [venue?.street, venue?.city, venue?.district, venue?.country].filter(Boolean);
  if (parts.length === 0) return null;

  const addressLine = [venue?.city, venue?.district, venue?.country].filter(Boolean).join(', ');

  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6, margin: '8px 0 12px', color: '#6b7280', fontSize: 14 }}>
      <MapPinIcon style={{ width: 16, marginTop: 2, flexShrink: 0 }} />
      <div>
        {venue?.street && <div style={{ color: '#374151', fontWeight: 500 }}>{venue.street}</div>}
        {addressLine && <div>{addressLine}</div>}
      </div>
    </div>
  );
});
VenueAddress.displayName = 'VenueAddress';

// Opening hours — collapsible
const OpeningHours = memo(({ openingHours }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const weekdayText = openingHours?.weekday_text;
  if (!weekdayText || weekdayText.length === 0) return null;

  return (
    <div style={{ marginBottom: 12 }}>
      <button
        onClick={() => setOpen((v) => !v)}
        style={{
          display: 'flex', alignItems: 'center', gap: 6, background: 'none',
          border: 'none', cursor: 'pointer', padding: 0, color: '#374151', fontSize: 14,
        }}
      >
        <ClockIcon style={{ width: 16 }} />
        <span style={{ fontWeight: 500 }}>{t('venue.openingHours')}</span>
        <span style={{ marginLeft: 4, color: '#9ca3af' }}>{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <div style={{ marginTop: 8, paddingLeft: 22 }}>
          {weekdayText.map((line, i) => (
            <div key={i} style={{ fontSize: 13, color: '#6b7280', lineHeight: 1.7 }}>{line}</div>
          ))}
        </div>
      )}
    </div>
  );
});
OpeningHours.displayName = 'OpeningHours';

// Contact section — only renders fields that actually have data
const ContactSection = memo(({ venue }) => {
  const { t } = useTranslation();
  const hasAny = venue?.phone || venue?.website || venue?.opening_hours?.weekday_text?.length;
  if (!hasAny) return null;

  const websiteUrl = venue?.website
    ? venue.website.startsWith('http') ? venue.website : `https://${venue.website}`
    : null;

  return (
    <div style={{ background: '#f9fafb', borderRadius: 16, padding: 20, marginBottom: 20 }}>
      <h3 style={{ fontSize: 16, fontWeight: 700, margin: '0 0 14px' }}>{t('venue.hoursContact')}</h3>

      <OpeningHours openingHours={venue?.opening_hours} />

      {venue?.phone && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
          <PhoneIcon style={{ width: 16, color: '#10b981', flexShrink: 0 }} />
          <a href={`tel:${venue.phone}`} style={{ fontSize: 14, color: '#2563eb', textDecoration: 'none', fontWeight: 500 }}>
            {venue.phone}
          </a>
        </div>
      )}

      {websiteUrl && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <GlobeAltIcon style={{ width: 16, color: '#3b82f6', flexShrink: 0 }} />
          <a
            href={websiteUrl} target="_blank" rel="noopener noreferrer"
            style={{ fontSize: 14, color: '#2563eb', textDecoration: 'none', fontWeight: 500 }}
          >
            {websiteUrl.replace(/^https?:\/\//, '')}
          </a>
        </div>
      )}
    </div>
  );
});
ContactSection.displayName = 'ContactSection';

// WiFi section — uses venue.wifi_networks from Neon response
const WifiSection = memo(({ wifi_networks }) => {
  const { t } = useTranslation();
  const [copiedId, setCopiedId] = useState(null);
  if (!wifi_networks || wifi_networks.length === 0) return null;

  const handleCopy = (id, password) => {
    if (!password) return;
    navigator.clipboard.writeText(password).then(() => {
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    });
  };

  const primary = wifi_networks.find((n) => n.is_primary_guest_ssid) || wifi_networks[0];
  const others = wifi_networks.filter((n) => n.id !== primary?.id);

  const renderNetwork = (network) => (
    <div
      key={network.id}
      style={{
        background: network.is_primary_guest_ssid ? '#eff6ff' : '#f9fafb',
        borderRadius: 12, padding: 14, marginBottom: 10,
        border: network.is_primary_guest_ssid ? '1px solid #bfdbfe' : '1px solid #e5e7eb',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
        <span style={{ fontSize: 14, fontWeight: 700, color: '#1f2937' }}>{network.ssid}</span>
        {network.is_primary_guest_ssid && (
          <span style={{
            fontSize: 11, fontWeight: 600, color: '#1d4ed8',
            background: '#dbeafe', borderRadius: 8, padding: '2px 8px',
          }}>
            {t('venue.wifiPrimaryBadge')}
          </span>
        )}
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          {network.auth_type && network.auth_type !== 'none' && (
            <span style={{ fontSize: 12, color: '#6b7280', display: 'flex', alignItems: 'center', gap: 4 }}>
              <LockClosedIcon style={{ width: 12 }} />{network.auth_type}
            </span>
          )}
          {network.is_free_for_visitors && (
            <span style={{ fontSize: 12, color: '#10b981', display: 'flex', alignItems: 'center', gap: 4 }}>
              <CheckCircleIcon style={{ width: 12 }} />{t('venue.freeForVisitors')}
            </span>
          )}
        </div>
        {network.password && (
          <button
            onClick={() => handleCopy(network.id, network.password)}
            style={{
              padding: '6px 14px', borderRadius: 8, border: 'none',
              background: copiedId === network.id ? '#10b981' : '#3b82f6',
              color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer',
            }}
          >
            {copiedId === network.id ? t('common.copied') : t('venue.copyPassword', { password: network.password })}
          </button>
        )}
      </div>
    </div>
  );

  return (
    <div style={{ background: '#f9fafb', borderRadius: 16, padding: 20, marginBottom: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
        <WifiIcon style={{ width: 18, color: '#3b82f6' }} />
        <h3 style={{ fontSize: 16, fontWeight: 700, margin: 0 }}>{t('venue.wifi')}</h3>
      </div>
      {renderNetwork(primary)}
      {others.map(renderNetwork)}
    </div>
  );
});
WifiSection.displayName = 'WifiSection';

// Star row helper

// Events section — uses venue.events from Neon response
const EventsSection = memo(({ events }) => {
  const { t } = useTranslation();
  if (!events || events.length === 0) return null;

  return (
    <div style={{ background: '#f9fafb', borderRadius: 16, padding: 20, marginBottom: 20 }}>
      <h3 style={{ fontSize: 16, fontWeight: 700, margin: '0 0 14px' }}>{t('venue.upcomingEvents')}</h3>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {events.slice(0, 5).map((ev, i) => (
          <div
            key={ev.id || i}
            style={{
              background: '#fff', borderRadius: 12, padding: 14,
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              border: '1px solid #e5e7eb',
            }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: '#111827', marginBottom: 3,
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {ev.title}
              </div>
              <div style={{ fontSize: 12, color: '#6b7280' }}>
                {formatDate(ev.start_date, t)}{ev.start_time ? t('venue.eventAtTime', { time: `${String(ev.start_time).slice(0,5)}${ev.end_time ? `–${String(ev.end_time).slice(0,5)}` : ''}` }) : ''}
              </div>
            </div>
            {ev.price_type && (
              <span style={{
                marginLeft: 12, fontSize: 12, fontWeight: 700, flexShrink: 0,
                color: ev.price_type === 'FREE' ? '#10b981' : '#ea580c',
              }}>
                {ev.price_type === 'FREE' ? t('event.free') : ev.price_type}
              </span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
});
EventsSection.displayName = 'EventsSection';

// Error screen
const ErrorScreen = memo(({ message, onBack }) => {
  const { t } = useTranslation();
  return (
    <div style={{
      minHeight: '100vh', background: '#f59e0b',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      color: '#fff', padding: 40, textAlign: 'center',
    }}>
      <div>
        <h2 style={{ fontSize: 24, marginBottom: 20 }}>{message}</h2>
        <button
          onClick={onBack}
          style={{
            padding: '12px 24px', background: '#fff', color: '#ea580c',
            border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer',
          }}
        >
          {t('venue.goBack')}
        </button>
      </div>
    </div>
  );
});
ErrorScreen.displayName = 'ErrorScreen';

// Loading skeleton
const LoadingSkeleton = () => (
  <div style={{
    minHeight: '100vh',
    background: 'linear-gradient(135deg,#fbbf24 0%,#f59e0b 50%,#ea580c 100%)',
    fontFamily: 'Inter,-apple-system,sans-serif',
  }}>
    <div style={{ padding: 20, display: 'flex', justifyContent: 'space-between' }}>
      <div style={{ width: 44, height: 44, borderRadius: '50%', background: 'rgba(255,255,255,.25)' }} />
    </div>
    <div style={{ margin: '0 20px', padding: 24, background: '#fff', borderRadius: 24 }}>
      <div style={{ height: 28, background: '#e5e7eb', borderRadius: 6, marginBottom: 12, width: '70%' }} />
      <div style={{ height: 16, background: '#e5e7eb', borderRadius: 6, marginBottom: 8, width: '40%' }} />
      <div style={{ height: 16, background: '#e5e7eb', borderRadius: 6, width: '55%' }} />
    </div>
  </div>
);

// ─── Main component ───────────────────────────────────────────────────────────

const VenueDetails = () => {
  const { t } = useTranslation();
  const { venueSlug } = useParams();
  const navigate = useNavigate();

  const [venue, setVenue] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [myDashboardRole, setMyDashboardRole] = useState(null);

  const handleBack = useCallback(() => navigate(-1), [navigate]);


  useEffect(() => {
    if (!venueSlug) return;
    let cancelled = false;
    setLoading(true);
    setError(null);

    fetchVenueBySlug(venueSlug)
      .then((v) => { if (!cancelled) { setVenue(v); setLoading(false); } })
      .catch((err) => { if (!cancelled) { setError(err.message); setLoading(false); } });

    return () => { cancelled = true; };
  }, [venueSlug]);

  // Check if current user is staff/manager/owner at this venue (for dashboard back-link)
  useEffect(() => {
    if (!venueSlug) return;
    const token = localStorage.getItem('venueapp_token') ||
      localStorage.getItem('venue_app_token') ||
      localStorage.getItem('token');
    if (!token) return;
    let cancelled = false;
    fetch(`${API_BASE_URL}/venue-dashboard/${encodeURIComponent(venueSlug)}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.ok ? r.json() : null)
      .then((data) => {
        if (!cancelled && data?.myRole) setMyDashboardRole(data.myRole);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [venueSlug]);

  if (loading) return <LoadingSkeleton />;
  if (error || !venue) return <ErrorScreen message={t('venue.notFound')} onBack={handleBack} />;

  const {
    name, google_rating, rating_count, price_level, business_status,
    primary_category_label, editorial_summary,
    media = [], wifi_networks = [], reviews = [], events = [],
    opening_hours, phone, website,
  } = venue;

  const isOperational = business_status === 'OPERATIONAL';

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg,#fbbf24 0%,#f59e0b 50%,#ea580c 100%)',
      fontFamily: 'Inter,-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif',
    }}>
      <StickyHeader onBack={handleBack} venue={venue} venueSlug={venueSlug} />

      {/* ── Gallery — full width, above the white card ── */}
      <VenueGallery venue={venue} neonMedia={media} />

      <div style={{ marginTop: -40, padding: '0 20px 24px', position: 'relative', zIndex: 10 }}>
        <motion.div
          initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
          style={{ background: '#fff', borderRadius: 24, padding: 24, boxShadow: '0 20px 40px rgba(0,0,0,.15)' }}
        >
          {/* ── Header ── */}
          <div style={{ marginBottom: 4 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
              <h2 style={{ fontSize: 22, fontWeight: 800, margin: 0, color: '#111827', flex: 1 }}>
                {name || t('venue.venueFallback')}
              </h2>
              {price_level != null && (
                <div style={{ marginLeft: 12, flexShrink: 0, paddingTop: 2 }}>
                  {priceSymbols(price_level)}
                </div>
              )}
            </div>

            {/* Rating row */}
            {google_rating != null && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                <StarIcon style={{ width: 18, color: '#fbbf24' }} />
                <span style={{ fontSize: 15, fontWeight: 700, color: '#111827' }}>
                  {Number(google_rating).toFixed(1)}
                </span>
                {rating_count != null && (
                  <span style={{ fontSize: 13, color: '#6b7280' }}>
                    {t('venue.reviewsCount', { count: rating_count })}
                  </span>
                )}
              </div>
            )}

            {/* Manage this venue — only visible to staff/manager/owner */}
            {myDashboardRole && (
              <a
                href={`/venue-dashboard/${venueSlug}`}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                  fontSize: 12, color: '#f97316', fontWeight: 600,
                  textDecoration: 'none', marginBottom: 6,
                  padding: '3px 10px', background: '#fff7ed',
                  borderRadius: 20, border: '1px solid #fed7aa',
                }}
              >
                ⚙️ {t('venue.manageThisVenue')}
              </a>
            )}

            {/* Address — NO fallbacks */}
            <VenueAddress venue={venue} />

            {/* Category / status badges */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
              {primary_category_label && (
                <span style={{
                  background: '#f3f4f6', color: '#374151',
                  padding: '4px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600,
                }}>
                  {primary_category_label}
                </span>
              )}
              {isOperational && (
                <span style={{
                  background: '#dcfce7', color: '#166534',
                  padding: '4px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600,
                  display: 'flex', alignItems: 'center', gap: 5,
                }}>
                  <span style={{ width: 6, height: 6, background: '#16a34a', borderRadius: '50%', display: 'inline-block' }} />
                  {t('venue.openNow')}
                </span>
              )}
            </div>

            {/* Editorial summary */}
            {editorial_summary && (
              <p style={{ fontSize: 14, color: '#6b7280', lineHeight: 1.6, margin: '0 0 12px' }}>
                {editorial_summary}
              </p>
            )}

            {/* EV-657 — removed the old "Test WiFi Speed" button (linked to the
                wrong /venues/:slug/speed-test surface). The real WiFi test is the
                CombinedScanSection widget. */}
          </div>

          {/* ── Contact & Hours ── */}
          <ContactSection venue={venue} />

          {/* ── WiFi ── */}
          <WifiSection wifi_networks={wifi_networks} />

          {/* ── Events ── */}
          <EventsSection events={events} />

          {/* ── Reviews ── */}
          <ReviewsSectionFull venue={venue} neonReviews={reviews} />
        </motion.div>
      </div>
    </div>
  );
};

export default VenueDetails;
